import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

interface LiveMusicProps {
  language: string;
}

const copy = {
  en: {
    eyebrow: 'EVERY SUNDAY',
    title: 'Live Music',
    lead: 'Bossa Nova in the garden',
    body:
      'Every Sunday, No Pátio fills with live Bossa Nova. Join us for a relaxed moment among the plants and flowers, carried by the gentle sound born by the sea — until the end of September.',
    pills: ['Live Bossa Nova', 'Every Sunday', 'Until end of September'],
    cta: 'Book a table',
  },
  pt: {
    eyebrow: 'AO DOMINGO',
    title: 'Música ao Vivo',
    lead: 'Bossa Nova no jardim',
    body:
      'Todos os domingos, o No Pátio enche-se de Bossa Nova ao vivo. Junte-se a nós para um momento descontraído entre plantas e flores, ao som suave que nasceu à beira-mar — até ao final de Setembro.',
    pills: ['Bossa Nova ao vivo', 'Todos os domingos', 'Até final de Setembro'],
    cta: 'Reservar mesa',
  },
};

export const LiveMusic = ({ language }: LiveMusicProps) => {
  const t = language === 'en' ? copy.en : copy.pt;

  return (
    <section id="live-music" className="live-music-section relative overflow-hidden py-16 lg:py-20">
      {/* Floating decorative notes */}
      <div className="notes" aria-hidden="true">
        <span className="note note-1">♪</span>
        <span className="note note-2">♫</span>
        <span className="note note-3">♩</span>
        <span className="note note-4">♬</span>
        <span className="note note-5">♪</span>
      </div>

      <div className="container mx-auto px-6 md:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="mx-auto max-w-3xl text-center"
        >
          {/* Guitar mark */}
          <div className="mark mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M14.5 3.5a1.5 1.5 0 0 1 2.12 0l1.88 1.88a1.5 1.5 0 0 1 0 2.12l-1.4 1.4 1.2 1.2a1 1 0 0 1-1.42 1.42l-1.2-1.2-5.1 5.1a4 4 0 1 1-1.42-1.42l5.1-5.1-1.2-1.2a1 1 0 0 1 1.42-1.42l1.2 1.2 1.4-1.4a.5.5 0 0 0 0-.7l-1.88-1.88a.5.5 0 0 0-.7 0"
                fill="var(--color-white)"
              />
              <circle cx="8" cy="16" r="2.2" fill="none" stroke="var(--color-white)" strokeWidth="1.4" />
            </svg>
          </div>

          <span className="eyebrow mb-2 inline-block text-sm font-semibold tracking-[0.25em]" style={{ color: 'var(--color-green)' }}>
            {t.eyebrow}
          </span>

          <h2 className="font-fleur text-4xl leading-tight md:text-5xl lg:text-6xl" style={{ color: 'var(--color-pink)' }}>
            {t.title}
          </h2>

          {/* pattern divider */}
          <div className="my-4 flex justify-center">
            <svg width="90" height="20" viewBox="0 0 90 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 10 L90 10" stroke="var(--color-pink)" strokeWidth="1" />
              <circle cx="45" cy="10" r="4" fill="var(--color-pink)" />
              <circle cx="25" cy="10" r="2" fill="var(--color-pink)" />
              <circle cx="65" cy="10" r="2" fill="var(--color-pink)" />
            </svg>
          </div>

          <p className="mb-5 text-xl font-medium md:text-2xl" style={{ color: 'var(--color-grey)', fontFamily: 'var(--font-heading)' }}>
            {t.lead}
          </p>

          <p className="mx-auto mb-8 max-w-xl text-sm leading-relaxed md:text-base" style={{ color: 'var(--color-grey)' }}>
            {t.body}
          </p>

          {/* detail pills */}
          <div className="mb-9 flex flex-wrap items-center justify-center gap-3">
            {t.pills.map((pill, i) => (
              <span
                key={i}
                className="pill inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold md:text-sm"
              >
                <span className="pill-dot" />
                {pill}
              </span>
            ))}
          </div>

          <Link to="/booking" className="cta-btn inline-flex items-center gap-2 rounded-full px-8 py-3 text-sm font-semibold md:text-base">
            {t.cta}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </motion.div>
      </div>

      <style>{`
        .live-music-section {
          background:
            radial-gradient(120% 90% at 50% -10%, rgba(197, 94, 134, 0.10) 0%, rgba(197, 94, 134, 0) 55%),
            radial-gradient(90% 80% at 100% 120%, rgba(76, 138, 90, 0.10) 0%, rgba(76, 138, 90, 0) 55%),
            #ffffff;
        }
        .live-music-section .mark {
          background: linear-gradient(135deg, var(--color-pink), var(--color-green));
          box-shadow: 0 12px 28px rgba(197, 94, 134, 0.28);
        }
        .live-music-section .pill {
          background: #ffffff;
          color: var(--color-grey);
          border: 1px solid rgba(76, 138, 90, 0.25);
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04);
        }
        .live-music-section .pill-dot {
          width: 8px;
          height: 8px;
          border-radius: 9999px;
          background: var(--color-pink);
          flex: 0 0 auto;
        }
        .live-music-section .cta-btn {
          background: var(--color-pink);
          color: #ffffff;
          box-shadow: 0 10px 24px rgba(197, 94, 134, 0.35);
          transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
        }
        .live-music-section .cta-btn:hover {
          background: var(--color-green);
          transform: translateY(-2px);
          box-shadow: 0 14px 30px rgba(76, 138, 90, 0.35);
        }
        .live-music-section .notes {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 1;
        }
        .live-music-section .note {
          position: absolute;
          font-size: 28px;
          color: var(--color-pink);
          opacity: 0;
          animation: floatNote 9s ease-in-out infinite;
        }
        .live-music-section .note-1 { left: 8%;  top: 30%; animation-delay: 0s;   color: var(--color-pink); }
        .live-music-section .note-2 { left: 18%; top: 70%; animation-delay: 1.6s; color: var(--color-green); font-size: 22px; }
        .live-music-section .note-3 { right: 12%; top: 26%; animation-delay: 0.8s; color: var(--color-green); }
        .live-music-section .note-4 { right: 20%; top: 66%; animation-delay: 2.4s; color: var(--color-pink); font-size: 34px; }
        .live-music-section .note-5 { left: 48%; top: 12%; animation-delay: 3.2s; color: var(--color-pink); font-size: 20px; }
        @keyframes floatNote {
          0%   { transform: translateY(10px) rotate(-6deg); opacity: 0; }
          25%  { opacity: 0.5; }
          50%  { transform: translateY(-18px) rotate(6deg); opacity: 0.6; }
          75%  { opacity: 0.4; }
          100% { transform: translateY(10px) rotate(-6deg); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .live-music-section .note { animation: none; opacity: 0.35; }
          .live-music-section .cta-btn:hover { transform: none; }
        }
      `}</style>
    </section>
  );
};

export default LiveMusic;
